/**
 * Das Auslösewort — eine Regel für Browser und Server.
 *
 * 23.09.2026, Ticket 2513: Die beiden Funktionen standen bis heute nur in
 * frontend/lib/jetzt-senden.ts. Jetzt braucht auch der Server sie: Endet
 * eine Aufnahme ohne Satzzeichen, bekommt das Modell eine Marke „mitten im
 * Satz" (server/pausen-marken.ts) — außer der letzte Satz IST das
 * Auslösewort. Martin sagt „… meine? Senden", und das „Senden" steht ohne
 * Punkt da. Der Server darf frontend/ nicht importieren (tsconfig.json
 * include), shared/ schon. Darum hierher verschoben und nicht kopiert;
 * jetzt-senden.ts reicht sie weiter.
 *
 * 26.09.2026, Tickets 2659 und 2674: Hier steht jetzt DIE Tabelle
 * „Endwort → Handlung“. Martin: *„neben Senden auch Go und Weiter"* — und
 * zwei Befehle, die den Text anders behandeln: „Verwerfen" und „ins
 * Textfeld". Alle Wege fragen dieselbe Stelle: die Karte mit der
 * Live-Mitschrift, die Karte mit dem Nachgehörten (Ticket 2672), das
 * Entfernen des Worts aus dem fertigen Text und die Pausen-Marke am Server.
 *
 * 26.09.2026 abends (Nachbesserung, Tickets 2674/2659): Ein Satzzeichen
 * macht ein mehrdeutiges Wort NICHT mehr zum Befehl. „Den Plan würde ich,
 * ehrlich gesagt, verwerfen." warf das ganze Diktat weg, „… nicht weiter,
 * [Denkpause] weil …" schickte mitten im Satz ab. Es zählt nur noch die
 * echte Sprechpause laut Mitschrift — oder das Wort ist das ganze Diktat.
 */

/**
 * Normalisiert ein Auslösewort für den Vergleich.
 *
 * ⚠️ Ohne Satzzeichen und Groß-/Kleinschreibung: der Erkenner schreibt
 *    „Jetzt senden." mit Punkt, der Mensch tippt „jetzt senden" ein.
 * ⚠️ NFC: ein „ü" kann aus einem oder aus zwei Zeichen bestehen. Verglichen
 *    wird Wort für Wort über Leerzeichen, nie über `\b` — das kennt nur
 *    ASCII und fände in „übernehmen" keine Wortgrenze.
 */
export function normWort(s: string): string {
  return s
    .normalize("NFC")
    .toLowerCase()
    .replace(/[.,!?;:…]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Ab so langer Ruhe vor dem letzten Wort steht es „für sich“ (Ticket 2659:
 * „Mach das … weiter" gegen „Mach weiter"). Gemeint ist die Lücke zwischen
 * den Wörtern — auf der Seite laut Mitschrift, am Server laut den Zeiten
 * seiner Erkennung (Ticket 2780, server/verwerfen-am-server.ts).
 */
export const PAUSE_VOR_WORT_MS = 1000;

/**
 * Die Einstellung `sendeWort` als Liste. (26.09.2026, Ticket 2659)
 *
 * Komma, Semikolon oder Zeilenumbruch trennen: „Senden, Go, Weiter". Ein
 * einzelner Wert wie „jetzt senden" bleibt EIN Eintrag — der gespeicherte
 * Stand braucht keinen Umzug.
 */
export function sendeWoerter(einstellung: string | null | undefined): string[] {
  const aus: string[] = [];
  for (const teil of (einstellung ?? "").split(/[,;\n]/)) {
    const w = normWort(teil);
    if (w && !aus.includes(w)) aus.push(w);
  }
  return aus;
}

/**
 * Endet der Text auf eines der Auslösewörter?
 *
 * ⚠️ Am ENDE, nicht irgendwo. „Kannst du mir das senden und dann …" enthält
 *    „senden" und ist keine Sendeaufforderung.
 * ⚠️ Nur die Wörter, die abschicken — die festen Befehle „Verwerfen" und
 *    „ins Textfeld" schicken nichts ab und gehören nicht hierher.
 * ⚠️ Aus DER Tabelle, samt Schreibweisen (26.09.2026 abends, Ticket 2659):
 *    die Pausen-Marke am Server fragte bis dahin nur die Einstellung und
 *    kannte „Gou" nicht.
 */
export function endetAufWort(text: string, einstellung: string | null | undefined): boolean {
  const t = normWort(text);
  return endwortTabelle(einstellung).some((e) => e.handlung === "senden" && endetAuf(t, e.wort));
}

function endetAuf(tNorm: string, wNorm: string): boolean {
  return tNorm === wNorm || tNorm.endsWith(` ${wNorm}`);
}

/**
 * Besteht das ganze Diktat nur aus Wörtern derselben Handlung? (Ticket 2780)
 *
 * „Stopp Stopp Stopp Verwerfen" ist genauso „das ganze Diktat ist der
 * Befehl" wie ein einzelnes „Verwerfen" — davor steht nichts, was Inhalt
 * sein könnte. Die Wörter kommen aus DER Tabelle, keine eigene Liste.
 */
function nurBefehle(tNorm: string, tabelle: readonly Endwort[], handlung: EndwortHandlung): boolean {
  const gleiche = tabelle.filter((x) => x.handlung === handlung);
  let rest = tNorm;
  while (rest) {
    const w = gleiche.find((x) => endetAuf(rest, x.wort));
    if (!w) return false;
    rest = rest === w.wort ? "" : rest.slice(0, -(w.wort.length + 1));
  }
  return true;
}

/** Was ein Endwort mit dem Diktat tut. */
type EndwortHandlung = "senden" | "verwerfen" | "textfeld";

export interface Endwort {
  /** Normalisiert (normWort). */
  wort: string;
  handlung: EndwortHandlung;
  /**
   * Auch ein Alltagswort? Dann zählt es erst als Befehl, wenn es für sich
   * steht (echte Sprechpause davor laut Mitschrift) — oder das ganze Diktat
   * ist. Ein Satzzeichen genügt NICHT (26.09.2026 abends).
   */
  mehrdeutig: boolean;
}

/**
 * Wörter, die auch Inhalt sind. (Ticket 2659)
 *
 * Martin sagt „Weiter", wenn er die offenen Fragen durchgeht — dort ist es
 * die Antwort. Und „Mach weiter" ist ein ganzer Satz.
 */
const MEHRDEUTIG: ReadonlySet<string> = new Set(["weiter", "go", "goh", "gou"]);

/** Wie die Erkennung ein Wort sonst noch schreibt (Ticket 2659: „Gou", „Goh"). */
const SCHREIBWEISEN: Readonly<Record<string, readonly string[]>> = {
  go: ["goh", "gou"],
};

/**
 * Die festen Befehle. (Ticket 2674)
 *
 * ⚠️ „Verwerfen" ist mehrdeutig: „Wir sollten das verwerfen." ist ein Satz,
 *    kein Befehl. Weil es das Diktat WEGWIRFT, gilt es nur, wenn es für
 *    sich steht — sonst ist es Inhalt. „Ins Textfeld" verliert nichts: der
 *    Text landet im Eingabefeld, und dort kann man ihn noch ändern.
 * ⚠️ 26.09.2026 abends: „Den Plan würde ich, ehrlich gesagt, verwerfen."
 *    galt wegen des Kommas als für sich stehend und warf alles weg. Jetzt
 *    nur noch nach einer echten Sprechpause (siehe `endwortTreffer`), und
 *    das Weggeworfene lässt sich eine Minute lang zurückholen
 *    (frontend/lib/verworfen.ts).
 */
/**
 * Gesprochenes „Verwerfen“ und „Zurückholen/Doch nicht“ — vorerst AUS.
 * (26.09.2026, 23:10)
 *
 * ⚠️ Vier Prüfrunden (pre.3345–3349) fanden jedes Mal einen Weg, auf dem
 *    Gesprochenes verloren ging — zuletzt: Ob die verworfene Aufnahme
 *    aufgehoben wird, hängt an der Tonwache der Seite, und die steht genau
 *    im Stillstand (Ticket 2634), in dem das Nachhören ein „Verwerfen“
 *    auslöst. Ein Befehl, der wegwirft, braucht einen Ton-Beweis, den die
 *    Seite dann nicht hat. Darum hier aus, bis es einen verlässlichen gibt.
 *    Der Knopf „Verwerfen“ in der Karte bleibt (dort entscheidet Martin
 *    selbst), ebenso „ins Textfeld“, „Senden“, „Go“, „Weiter“.
 * Wieder an: diesen Schalter umlegen — die Tests dazu laufen dann wieder mit.
 */
export const VERWERFEN_PER_SPRACHE = false;

/**
 * Die Befehle, die das Diktat wegwerfen. (Ticket 2674; „Stopp" seit
 * 29.09.2026, Ticket 2780)
 *
 * Martin sagte „Stopp Stopp Stopp Verwerfen" und erwartete: *„einfach nur
 * den Prozess stoppen und nichts verschicken"*. „Stopp" ist so mehrdeutig
 * wie „Verwerfen" — „… stopp mal kurz, weil …" ist ein Satz. Es gilt darum
 * nur am Ende und nur für sich (Pause davor oder nichts als Befehle).
 *
 * ⚠️ Wo sie gelten, sagt `verwerfenGilt` (EndwortOptionen): auf der Seite
 *    nur mit VERWERFEN_PER_SPRACHE, am Server an der Erkennung der GANZEN
 *    Aufnahme — die liegt dort (audio_url) und auf der Seite zum
 *    Zurückholen; der fehlende Ton-Beweis der Seite spielt dort keine Rolle.
 */
const VERWERFEN_BEFEHLE: readonly Endwort[] = [
  { wort: "verwerfen", handlung: "verwerfen", mehrdeutig: true },
  { wort: "stopp", handlung: "verwerfen", mehrdeutig: true },
  { wort: "stop", handlung: "verwerfen", mehrdeutig: true },
];

const BEFEHLE: readonly Endwort[] = [
  { wort: "ins textfeld", handlung: "textfeld", mehrdeutig: false },
  { wort: "ins text feld", handlung: "textfeld", mehrdeutig: false },
  { wort: "ins text-feld", handlung: "textfeld", mehrdeutig: false },
];

/**
 * DIE Tabelle „Endwort → Handlung“ für diese Einstellung.
 *
 * ⚠️ Die festen Befehle gelten nur, wo gesprochene Befehle eingeschaltet
 *    sind — also ein Auslösewort eingetragen ist. Wer das nie wollte, dem
 *    verschwindet kein Diktat, weil es zufällig auf „verwerfen" endet.
 * ⚠️ Längste zuerst: „jetzt senden" vor „senden".
 */
export function endwortTabelle(
  einstellung: string | null | undefined,
  verwerfenGilt: boolean = VERWERFEN_PER_SPRACHE,
): Endwort[] {
  const senden = sendeWoerter(einstellung);
  if (senden.length === 0) return [];
  const tabelle: Endwort[] = [...(verwerfenGilt ? VERWERFEN_BEFEHLE : []), ...BEFEHLE];
  const schonDa = (w: string): boolean => tabelle.some((e) => e.wort === w);
  for (const w of senden) {
    for (const s of [w, ...(SCHREIBWEISEN[w] ?? [])]) {
      if (!schonDa(s)) tabelle.push({ wort: s, handlung: "senden", mehrdeutig: MEHRDEUTIG.has(s) });
    }
  }
  return tabelle.sort((a, b) => b.wort.split(" ").length - a.wort.split(" ").length);
}

export interface EndwortTreffer {
  handlung: EndwortHandlung;
  /** Das getroffene Wort, normalisiert. */
  wort: string;
  /** Ist es ein Alltagswort (siehe Endwort.mehrdeutig)? */
  mehrdeutig: boolean;
  /** Besteht das Diktat nur aus dem Wort? */
  allein: boolean;
  /**
   * Steht es für sich — allein, oder nach einer echten Sprechpause laut
   * Mitschrift? Ein Satzzeichen zählt nicht (26.09.2026 abends).
   */
  fuerSich: boolean;
  /**
   * Der Text ohne das Endwort, wo es wegfällt.
   *
   *   senden     wie bisher weg; ein mehrdeutiges nur, wenn es für sich
   *              steht — und besteht das Diktat NUR daraus, bleibt es: es
   *              IST dann der Inhalt („Weiter" in der Fragenrunde)
   *   textfeld   weg
   *   verwerfen  was weggelegt wird, ohne den Befehl — geschickt wird
   *              nichts, zurückholen lässt es sich eine Minute lang
   */
  text: string;
}

export interface EndwortOptionen {
  /**
   * Lag laut Mitschrift eine Sprechpause vor dem letzten Wort? `davor` ist
   * der normalisierte Text vor dem Endwort. Ohne Angabe gibt es keine —
   * dann steht ein mehrdeutiges Wort nur für sich, wenn es allein ist.
   */
  pause?: (davor: string) => boolean;
  /**
   * Der Text ist nur das ENDSTÜCK der Aufnahme, und davor war schon Ton
   * (Nachhören, Ticket 2672: der Server erkennt nur die letzten 10 s).
   * (26.09.2026 spät, dritte Runde)
   *
   * Dann ist kein Wort „das ganze Diktat" — was davor gesagt wurde, steht
   * gar nicht im Text. Es gilt nur noch ein eindeutiges Absendewort
   * („Senden"); „Weiter", „Go", „Verwerfen" und „ins Textfeld" sind hier
   * kein Befehl.
   */
  tonDavor?: boolean;
  /**
   * Gelten „Verwerfen" und „Stopp" hier als Befehl? Ohne Angabe nur mit
   * VERWERFEN_PER_SPRACHE. Der Server setzt es für die Erkennung der ganzen
   * Aufnahme (Ticket 2780) — und wo die Angabe sagt, dass ein Verwerfen
   * ausgelöst hat (Zurückholen).
   */
  verwerfenGilt?: boolean;
}

/**
 * Endet der Text auf ein Endwort — und was folgt daraus?
 *
 * `null`, wenn keines am Ende steht. Auch `null` für ein „verwerfen", das
 * NICHT für sich steht: dann ist es Inhalt, und es gilt, was sonst gilt.
 *
 * ⚠️ „Für sich" heißt seit 26.09.2026 abends: allein, oder eine echte
 *    Sprechpause davor (`opt.pause`). Das Satzzeichen des Erkenners zählt
 *    nicht mehr — er setzt Kommas, wo niemand innehielt.
 */
export function endwortTreffer(
  text: string,
  einstellung: string | null | undefined,
  opt: EndwortOptionen = {},
): EndwortTreffer | null {
  const t = normWort(text);
  if (!t) return null;
  const tabelle = endwortTabelle(einstellung, opt.verwerfenGilt ?? VERWERFEN_PER_SPRACHE);
  const e = tabelle.find((x) => endetAuf(t, x.wort));
  if (!e) return null;
  // Nur das Endstück gehört (tonDavor): nur ein eindeutiges Absendewort gilt.
  if (opt.tonDavor && (e.handlung !== "senden" || e.mehrdeutig)) return null;
  // Ticket 2780: „Stopp Stopp Verwerfen" ist so allein wie „Verwerfen" —
  // nur beim Verwerfen. „weiter weiter" wartet weiter die volle Ruhe ab
  // (jetzt-senden.ts), wie vorher.
  const allein =
    !opt.tonDavor &&
    (t === e.wort || (e.handlung === "verwerfen" && nurBefehle(t, tabelle, e.handlung)));
  const roh = text.trim().split(/\s+/);
  const weg = e.wort.split(" ").length;
  // Stehen einzelne Satzzeichen als eigenes Stück da („morgen , senden"),
  // zählen sie beim Abschneiden nicht als Wort.
  let bis = roh.length;
  for (let n = 0; n < weg && bis > 0; ) {
    bis--;
    if (normWort(roh[bis] ?? "")) n++;
  }
  const vorher = roh.slice(0, bis);
  const davor = normWort(vorher.join(" "));
  const fuerSich = allein || (opt.pause?.(davor) ?? false);
  if (e.handlung === "verwerfen" && !fuerSich) return null;
  const ohne = vorher.join(" ").replace(/[\s,;:–—-]+$/u, "").trim();
  let rest: string;
  if (e.handlung === "senden" && e.mehrdeutig) rest = allein || !fuerSich ? text.trim() : ohne;
  else rest = allein ? "" : ohne;
  return { handlung: e.handlung, wort: e.wort, mehrdeutig: e.mehrdeutig, allein, fuerSich, text: rest };
}

/**
 * Der Text ohne das Endwort — nur, wenn er auf ein Wort DIESER Handlung
 * endet. Alles andere bleibt, wie es ist: wer „… verwerfen" sagt und dann
 * den Knopf „Senden" drückt, hat „verwerfen" gemeint, wie es dasteht.
 */
export function ohneEndwort(
  text: string,
  einstellung: string | null | undefined,
  handlung: EndwortHandlung,
  opt: EndwortOptionen = {},
): string {
  const t = endwortTreffer(text, einstellung, opt);
  return t?.handlung === handlung ? t.text : text;
}

/**
 * Welcher Befehl hat die Aufnahme beendet — und stand er für sich? Reist
 * von der Karte bis zum Server mit. (26.09.2026 spät, dritte Runde)
 */
export interface EndwortAngabe {
  handlung: EndwortHandlung;
  fuerSich: boolean;
}

/**
 * Der Text ohne den Befehl, der ihn SELBST ausgelöst hat — die EINE Regel
 * für die Anzeige, den gesendeten Text und den Text am Server.
 * (26.09.2026 spät, dritte Runde)
 *
 * ⚠️ Bis pre.3347 fiel das Wort nur aus der Blase: der Server gab dem
 *    Modell und dem Verlauf „…, senden." weiter. Jetzt wendet der Server
 *    diese Funktion auf SEINE Erkennung an, und die Seite zeigt, was er
 *    zurückgibt (nur ein Server ohne sie — vor dem Neustart — lässt die
 *    Seite es selbst tun, mit derselben Funktion).
 * ⚠️ `null` (Knopf, Stille, Erneut senden ohne Wort): nichts fällt weg —
 *    per Knopf geht „Gut. Weiter." unverändert hinaus.
 */
export function ohneAusgeloestesEndwort(
  text: string,
  einstellung: string | null | undefined,
  angabe: EndwortAngabe | null | undefined,
): string {
  if (!angabe) return text;
  return ohneEndwort(text, einstellung, angabe.handlung, {
    pause: () => angabe.fuerSich,
    // Ticket 2780: hat ein Verwerfen ausgelöst (am Server), kennt die
    // Tabelle es auch — sonst bliebe das Wort beim Zurückholen stehen.
    ...(angabe.handlung === "verwerfen" ? { verwerfenGilt: true } : {}),
  }).trim();
}

const HANDLUNGEN: readonly EndwortHandlung[] = ["senden", "verwerfen", "textfeld"];

/** Die Angabe aus einem Formularfeld (JSON) — alles Unbekannte ist `null`. */
export function leseEndwortAngabe(roh: unknown): EndwortAngabe | null {
  if (typeof roh !== "string" || roh.length > 200) return null;
  try {
    const j = JSON.parse(roh) as { handlung?: unknown; fuerSich?: unknown } | null;
    if (!j || typeof j.fuerSich !== "boolean") return null;
    const h = HANDLUNGEN.find((x) => x === j.handlung);
    return h ? { handlung: h, fuerSich: j.fuerSich } : null;
  } catch {
    return null;
  }
}

/**
 * „Zurückholen" — das nächste Diktat holt das Verworfene zurück. (26.09.2026
 * abends, Ticket 2674)
 *
 * Nur, wenn das GANZE Diktat eines dieser Wörter ist: „Hol das zurück, was
 * ich gestern gesagt habe" ist ein Satz, kein Befehl. Ob etwas zum
 * Zurückholen daliegt, weiß nur die Seite (frontend/lib/verworfen.ts) —
 * hier steht nur, welche Wörter es sind.
 */
const ZURUECKHOLEN: readonly string[] = ["zurückholen", "zurück holen", "doch nicht"];

export function istZurueckholen(text: string): boolean {
  // Ohne gesprochenes Verwerfen auch kein gesprochenes Zurückholen — sonst
  // schluckt „Doch nicht.“ als Antwort auf eine Frage ein echtes Diktat.
  // Zurückholen per Tipp auf die Notiz bleibt.
  if (!VERWERFEN_PER_SPRACHE) return false;
  return ZURUECKHOLEN.includes(normWort(text));
}
